import React, { useMemo, useState } from 'react'
import { useGetSubscriptionQuery } from '../../../../../features/fieldMapper/fieldMapperApi'
import { EmptyState, ErrorState, LoadingState } from '../Shared/Components/MappingStates'
import Shared from '../Shared/mappingUi.module.css'
import TabStyle from '../mappingTabs.module.css'
import GraphMapperApp from './graph/GraphMapperApp'
import { useUnsavedChanges } from './UnsavedChangesGuard'
import Style from './FieldMapper.module.css'
import { CLIENT_PERMISSION } from '../../../../../features/clientUsers/clientAccess'
import { useSubscriptionAccess } from '../../../../../features/clientUsers/useClientAccess'
import { Warehouse } from '@mui/icons-material';

const ENTITY_ORDER = ['Product', 'Customer', 'Sales Order', 'Shipment', 'Invoice', 'Inventory']

/**
 * Payment, Shipping and Sales Person have no backend schema; they map through the
 * shipping/payment/salesPerson-mappings endpoints.
 */
const VIRTUAL_ENTITIES = [
    { entityId: 'virtual-payment', entityName: 'Payment', isVirtual: true, parentEntityName: 'Order' },
    { entityId: 'virtual-shipping', entityName: 'Shipping', isVirtual: true, parentEntityName: 'Order' },
    // Sales person mappings are connection-level, so the entity is listed even when there is no Customer entity
    { entityId: 'virtual-salesperson', entityName: 'Sales Person', isVirtual: true, parentEntityName: 'Customer', showWithoutParent: true }
]

/** Backend entity names that host each parentEntityName ('Sales Order' counts as 'Order'). */
const PARENT_ALIASES = { order: 'order', 'sales order': 'order', customer: 'customer' }

const ENTITY_ICONS = {
    product: 'uil-box',
    customer: 'uil-user-circle',
    'sales order': 'uil-shopping-cart',
    order: 'uil-shopping-cart',
    shipment: 'uil-truck',
    shipping: 'uil-truck',
    invoice: 'uil-invoice',
    inventory: 'uil-layer-group',
    payment: 'uil-credit-card',
    'sales person': 'uil-users-alt'
}

const entityIcon = (entityName) => ENTITY_ICONS[(entityName || '').toLowerCase()] || 'uil-sitemap'

const orderIndex = (entityName) => {
    const index = ENTITY_ORDER.indexOf(entityName)
    return index === -1 ? ENTITY_ORDER.length : index
}

/** "Sales Person", "SalesPerson" and "salesperson" all compare equal. */
const nameKey = (entityName) => (entityName || '').toLowerCase().replace(/[\s_-]+/g, '')

const VIRTUAL_BY_NAME = VIRTUAL_ENTITIES.reduce((byName, virtual) => ({ ...byName, [nameKey(virtual.entityName)]: virtual }), {})

/**
 * Sorts by ENTITY_ORDER (unknown entities last) and inserts each virtual entity right after its parent entity.
 * A backend entity with a virtual entity's name has no schema to map, so the virtual one takes its place;
 * virtual entities flagged showWithoutParent are appended at the end when their parent is missing.
 */
const toDisplayedEntities = (entities) => {
    const result = []
    const added = new Set()
    const sorted = [...entities].sort((left, right) => orderIndex(left.entityName) - orderIndex(right.entityName))
    sorted.forEach((entity) => {
        const virtualMatch = VIRTUAL_BY_NAME[nameKey(entity.entityName)]
        if (virtualMatch) {
            if (!added.has(virtualMatch.entityId)) {
                result.push(virtualMatch)
                added.add(virtualMatch.entityId)
            }
            return
        }
        result.push(entity)
        const parentKey = PARENT_ALIASES[entity.entityName ? entity.entityName.toLowerCase() : '']
        if (parentKey) {
            VIRTUAL_ENTITIES
                .filter((virtual) => virtual.parentEntityName.toLowerCase() === parentKey && !added.has(virtual.entityId))
                .forEach((virtual) => {
                    result.push(virtual)
                    added.add(virtual.entityId)
                })
        }
    })
    return result.concat(VIRTUAL_ENTITIES.filter((virtual) => virtual.showWithoutParent && !added.has(virtual.entityId)))
}

/**
 * Connections API instance flag that enables each entity (keyed by nameKey).
 * Entities without a flag here (Shipment, Invoice, ...) are not shown.
 */
const ENTITY_FLAGS = {
    product: 'isProductAttributeMappingEnable',
    customer: 'isCustomerAttributeMappingEnable',
    salesorder: 'isSalesOrderAttributeMappingEnable',
    order: 'isSalesOrderAttributeMappingEnable',
    Warehouse: 'isMultiWarehouseAttributeMappingEnable',
    shipping: 'isShippingMethodMappingEnable',
    payment: 'isPaymentMethodMappingEnable',
    salesperson: 'isSalesPersonMappingEnable'
}

const isEntityEnabled = (entity, instance) => {
    const flag = ENTITY_FLAGS[nameKey(entity.entityName)]
    return Boolean(flag && instance?.[flag])
}

/** Sidebar: the entity list, styled like the Mapping Types picker it replaces. */
const EntityList = ({ title, entities, selectedId, isLoading, onSelect, onCollapse }) => (
    <div className={`${TabStyle.panel} ${Style.sidebarInner}`}>
        <div className={Style.sidebarHeader}>
            <p className={`${TabStyle.panelTitle} ${Style.sidebarTitle}`}>{title}</p>
            <button type="button" className={Style.iconButton} onClick={onCollapse} aria-label="Collapse sidebar" title="Collapse sidebar">
                <i className="uil-angle-left" aria-hidden="true" />
            </button>
        </div>
        {isLoading ? (
            <ul className={TabStyle.list} aria-hidden="true">
                {Array.from({ length: 6 }).map((_, index) => (
                    <li key={`entity-skeleton-${index}`} className={Style.skeletonItem} />
                ))}
            </ul>
        ) : entities.length === 0 ? (
            <p className={Style.listEmpty}>No entities available.</p>
        ) : (
            <nav aria-label="Mapping entities">
                <ul className={TabStyle.list}>
                    {entities.map((entity) => {
                        const isActive = entity.entityId === selectedId
                        return (
                            <li key={entity.entityId}>
                                <button
                                    type="button"
                                    className={[TabStyle.item, isActive ? TabStyle.itemActive : ''].join(' ').trim()}
                                    aria-current={isActive ? 'page' : undefined}
                                    onClick={() => onSelect(entity.entityId)}
                                >
                                    <span className={TabStyle.icon}>
                                        <i className={entityIcon(entity.entityName)} aria-hidden="true" />
                                    </span>
                                    <span className={TabStyle.text}>
                                        <span className={TabStyle.label}>{entity.entityName}</span>
                                        {entity.isVirtual ? (
                                            <span className={TabStyle.description}>{`Part of ${entity.parentEntityName}`}</span>
                                        ) : null}
                                    </span>
                                    <i className={`uil-angle-right ${TabStyle.chevron}`} aria-hidden="true" />
                                </button>
                            </li>
                        )
                    })}
                </ul>
            </nav>
        )}
    </div>
)

/**
 * mode 'entities' lists the backend entities (Product, Customer, ...); mode 'virtual' lists the
 * virtual ones (Payment, Shipping, Sales Person), which have no schema, so no entity fetch is needed.
 */
const FieldMapper = ({ connectorId, instancesId, instance, sourceShortName, targetShortName, mode = 'entities', title }) => {
    const isVirtualMode = mode === 'virtual'
    const subscriptionId = instance?.subscriptionId ?? instance?.subscriptionid
    const { can } = useSubscriptionAccess(subscriptionId)
    const { data, isLoading, isError } = useGetSubscriptionQuery(subscriptionId, { skip: !subscriptionId || isVirtualMode })
    const [selectedEntityId, setSelectedEntityId] = useState(null)
    const [showSidebar, setShowSidebar] = useState(true)
    // Header element the graph toolbar, direction toggle and search portal into
    const [toolbarSlot, setToolbarSlot] = useState(null)

    const entities = useMemo(() => {
        const candidates = isVirtualMode
            ? VIRTUAL_ENTITIES
            : toDisplayedEntities(Array.isArray(data?.entities) ? data.entities : []).filter((entity) => !entity.isVirtual)
        return candidates.filter((entity) => isEntityEnabled(entity, instance))
    }, [data, instance, isVirtualMode])
    // Keep the chosen entity while it still exists, otherwise fall back to the first one.
    const selected = entities.find((entity) => entity.entityId === selectedEntityId) || entities[0] || null

    // Switching entity drops the open mapping, so unsaved changes have to be saved or discarded first
    const { confirmLeave } = useUnsavedChanges()
    const selectEntity = (entityId) => {
        if (selected && entityId === selected.entityId) {
            return
        }
        confirmLeave(() => setSelectedEntityId(entityId))
    }

    const source = sourceShortName || 'ERP'
    const target = targetShortName || 'eCommerce'
    const workflow = useMemo(
        () => ({
            name: `${source} to ${target}`,
            description: 'Connector mapping customization workspace',
            business_context: { erp: source, ecommerce: target },
            ai_response: {
                connectorId,
                instanceId: instancesId,
                subscriptionid: subscriptionId,
                business_context: { erp: source, ecommerce: target },
                recommended_flows: [{ name: source, erp: source, ecommerce: target }]
            }
        }),
        [connectorId, instancesId, subscriptionId, source, target]
    )

    const renderBody = () => {
        if (!subscriptionId) {
            return (
                <EmptyState
                    icon="uil-sitemap"
                    message="Field Mapper is not available for this connection."
                    hint="No subscription is linked to this connector instance."
                />
            )
        }
        if (isLoading) {
            return <LoadingState message="Loading entities..." />
        }
        if (isError) {
            return <ErrorState message="Unable to load the entities for this connection." />
        }
        if (!selected) {
            return (
                <EmptyState
                    icon="uil-layer-group"
                    message={isVirtualMode ? 'No mappings are enabled for this connection.' : 'No mapping entities available yet.'}
                    hint={isVirtualMode
                        ? 'Payment, Shipping and Sales Person mappings will appear here once they are enabled for this connector.'
                        : 'Entity-level mapping controls will appear here once this connector exposes customizable entities.'}
                />
            )
        }
        return (
            <div className={Style.canvas}>
                <GraphMapperApp key={selected.entityId} workflow={workflow} selectedEntity={selected} toolbarSlot={toolbarSlot} canEdit={can(CLIENT_PERMISSION.MAPPING_WRITE)} />
            </div>
        )
    }

    return (
        <div className={Style.layout}>
            {showSidebar ? (
                <aside className={Style.sidebar}>
                    <EntityList
                        title={title || 'Entities'}
                        entities={entities}
                        selectedId={selected ? selected.entityId : null}
                        isLoading={Boolean(subscriptionId) && isLoading}
                        onSelect={selectEntity}
                        onCollapse={() => setShowSidebar(false)}
                    />
                </aside>
            ) : null}
            <section className={Style.main}>
                <div className={`card-header p-10 ${Shared.header}`}>
                    {!showSidebar ? (
                        <div className={Shared.headerTitle}>
                            <button type="button" className={Style.iconButton} onClick={() => setShowSidebar(true)} aria-label="Show entities" title="Show entities">
                                <i className="uil-list-ul" aria-hidden="true" />
                            </button>
                        </div>
                    ) : null}
                    <div ref={setToolbarSlot} className={Style.headerSlot} />
                </div>
                <div className={`p-10 ${Shared.body} ${Style.mainBody}`}>{renderBody()}</div>
            </section>
        </div>
    )
}

export default FieldMapper
